/**
 * ConfirmModal — confirmation dialog before applying / discarding an
 * assistant proposal (Story 8.5 AC3).
 *
 * Renders a summary of the merged prompt + proposed changes count + the
 * activation flag (when applicable). Uses the same shadcn `Dialog`
 * primitives as `ActivateConfirmDialog` so the UX stays consistent across
 * the prompts surface.
 *
 * The modal is dumb: callers fetch the preview (POST `/preview`) BEFORE
 * opening and pass `proposedSystemPrompt` for context. The CTA simply
 * triggers `onConfirm()` and the parent runs the apply/discard mutation.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ProposedChange } from '@/lib/promptAssistant';

export type ConfirmAction = 'apply' | 'apply-activate' | 'discard';

/**
 * Story 8.5b — minimal validation report shape rendered inline when the
 * apply mutation comes back 422 VALIDATION_FAILED. Matches the backend
 * `ValidationReport` from PromptSafetyValidator (kept loose because the
 * report payload already passes Ajv on the wire).
 */
export interface ValidationFailedReportLike {
  passed: boolean;
  severity: string | null;
  blockers: Array<{ name: string; severity: string; message?: string }>;
  warnings: Array<{ name: string; severity: string; message?: string }>;
}

export interface ConfirmModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  action: ConfirmAction;
  /** Proposed prompt diffs — used only for the count summary. */
  proposedChanges: ProposedChange[];
  /** Proposed merged system prompt — shown as a preview block (apply only). */
  proposedSystemPrompt?: string;
  /** True while the parent is running the underlying mutation. */
  pending?: boolean;
  /**
   * Story 8.5b — when the parent's apply mutation comes back with
   * 422 VALIDATION_FAILED, the `error.report` is passed here. Modal stays
   * open and renders the blockers/warnings inline so the operator sees
   * concrete reasons instead of a generic toast.
   */
  validationFailedReport?: ValidationFailedReportLike | null;
  onConfirm: () => void;
}

const TITLES: Record<ConfirmAction, string> = {
  apply: 'Aplicar proposta?',
  'apply-activate': 'Aplicar e ativar nova versão?',
  discard: 'Descartar proposta?',
};

const DESCRIPTIONS: Record<ConfirmAction, string> = {
  apply:
    'Cria uma nova versão do prompt a partir das mudanças propostas. A versão atual permanece ativa até alguém ativar a nova manualmente.',
  'apply-activate':
    'Cria uma nova versão E flipa o prompt ativo. Conversas em andamento continuam usando a versão antiga (snapshot pinning); apenas conversas NOVAS usarão a versão criada.',
  discard:
    'Marca a sessão como descartada. As mensagens permanecem para auditoria, mas nenhuma versão é criada e a proposta não pode mais ser aplicada.',
};

const CTA_LABEL: Record<ConfirmAction, string> = {
  apply: 'Aplicar',
  'apply-activate': 'Aplicar e ativar',
  discard: 'Descartar',
};

export function ConfirmModal({
  open,
  onOpenChange,
  action,
  proposedChanges,
  proposedSystemPrompt,
  pending = false,
  validationFailedReport = null,
  onConfirm,
}: ConfirmModalProps) {
  const isDiscard = action === 'discard';
  const hasValidationError = validationFailedReport !== null && !validationFailedReport.passed;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assistant-confirm-modal" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{TITLES[action]}</DialogTitle>
          <DialogDescription>{DESCRIPTIONS[action]}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>
              Mudanças propostas: <strong>{proposedChanges.length}</strong>
            </span>
            {action === 'apply-activate' ? (
              <span className="rounded bg-amber-500/10 px-2 py-0.5 text-amber-700 dark:text-amber-400">
                Ativação imediata
              </span>
            ) : null}
          </div>

          {!isDiscard && proposedSystemPrompt ? (
            <details className="rounded border p-2">
              <summary className="cursor-pointer text-xs font-medium">
                Pré-visualização do prompt resultante
              </summary>
              <pre className="mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap rounded bg-muted/50 p-2 text-[11px]">
                {proposedSystemPrompt}
              </pre>
            </details>
          ) : null}

          {hasValidationError ? (
            <div
              data-testid="assistant-confirm-modal-validation-error"
              className="rounded border border-destructive/50 bg-destructive/10 p-3 text-xs"
            >
              <div className="font-semibold text-destructive">
                Validação falhou — corrija e tente novamente
              </div>
              {validationFailedReport!.blockers.length > 0 ? (
                <ul className="mt-2 list-disc space-y-1 pl-4 text-destructive">
                  {validationFailedReport!.blockers.map((b, i) => (
                    <li key={`b-${i}`}>
                      <strong>[{b.severity}]</strong> {b.name}
                      {b.message ? <>: {b.message}</> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              {validationFailedReport!.warnings.length > 0 ? (
                <ul className="mt-2 list-disc space-y-1 pl-4 text-amber-700 dark:text-amber-400">
                  {validationFailedReport!.warnings.map((w, i) => (
                    <li key={`w-${i}`}>
                      <strong>[{w.severity}]</strong> {w.name}
                      {w.message ? <>: {w.message}</> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant={isDiscard ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={pending}
            data-testid="assistant-confirm-modal-cta"
          >
            {pending ? 'Processando…' : hasValidationError ? 'Tentar novamente' : CTA_LABEL[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
